<template>
  <div class="lp-page">
    <nav class="landing-nav" :class="{ scrolled: navScrolled }">
      <div class="nav-inner">
        <div class="nav-logo" @click="goHome">
          <div class="logo-mark">Z</div>
          <span class="logo-text">智磐 AI</span>
        </div>

        <div class="nav-actions">
          <a class="nav-link" :class="{ active: isActive('/home/services') }" @click.prevent="goServices">企业服务</a>
          <a class="nav-link" :class="{ active: isActive('/home/usage') }" @click.prevent="goUsage">使用说明</a>
          <button class="btn-primary" @click="goApp">即刻开始</button>
        </div>

        <button class="mobile-toggle" @click="mobileOpen = !mobileOpen">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
      </div>

      <div v-if="mobileOpen" class="mobile-menu">
        <a class="mobile-link" :class="{ active: isActive('/home/services') }" @click.prevent="goServices(); mobileOpen = false">企业服务</a>
        <a class="mobile-link" :class="{ active: isActive('/home/usage') }" @click.prevent="goUsage(); mobileOpen = false">使用说明</a>
        <button class="btn-primary mobile-cta" @click="goApp">即刻开始</button>
      </div>
    </nav>

    <main class="lp-main">
      <section class="lp-hero">
        <div class="lp-container">
          <span class="lp-badge">川岳智磐</span>
          <h1>企业服务介绍</h1>
          <p>
            川岳智磐专注于跨境电商 AI 生产力建设，为品牌、卖家与服务商提供
            “素材生成 + 运营提效 + 系统协同”的一体化能力。我们通过智磐 AI 工作台，
            帮助团队把设计与内容生产从人力密集型转为流程化、数据化。
          </p>
          <div class="lp-kpi">
            <div class="lp-kpi-item">
              <span class="lp-kpi-num">跨团队</span>
              <span class="lp-kpi-label">支持运营 / 设计 / 投放协作</span>
            </div>
            <div class="lp-kpi-item">
              <span class="lp-kpi-num">全链路</span>
              <span class="lp-kpi-label">图文视频一体化生产</span>
            </div>
            <div class="lp-kpi-item">
              <span class="lp-kpi-num">可扩展</span>
              <span class="lp-kpi-label">支持企业级流程定制</span>
            </div>
          </div>
        </div>
      </section>

      <section class="lp-section">
        <div class="lp-container">
          <h2 class="lp-section-title">服务矩阵</h2>
          <p class="lp-section-desc">
            从工具落地到流程共建，覆盖企业在 AI 内容生产上的关键节点。
          </p>
          <div class="lp-grid-4">
            <article class="lp-card" v-for="service in serviceMatrix" :key="service.title">
              <h3>{{ service.title }}</h3>
              <p>{{ service.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="lp-section">
        <div class="lp-container">
          <h2 class="lp-section-title">合作流程</h2>
          <p class="lp-section-desc">
            标准化交付机制，确保需求快速落地并可持续迭代。
          </p>
          <div class="lp-steps">
            <article class="lp-step" v-for="step in processSteps" :key="step.title">
              <span class="lp-step-num">{{ step.no }}</span>
              <div>
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="lp-section">
        <div class="lp-container">
          <h2 class="lp-section-title">适配场景</h2>
          <p class="lp-section-desc">覆盖跨境电商团队常见业务形态与阶段性需求。</p>
          <div class="lp-grid-3">
            <article class="lp-card" v-for="scene in scenarios" :key="scene.title">
              <h3>{{ scene.title }}</h3>
              <p>{{ scene.desc }}</p>
            </article>
          </div>
          <div class="lp-highlight">
            我们建议先从“一个品类、一条内容链路”启动试点，再扩展到多店铺与多站点，
            以更低风险实现组织级 AI 升级。
          </div>
        </div>
      </section>
    </main>

    <footer class="lp-footer">
      <div class="lp-container lp-footer-inner">
        <a class="lp-logo" @click.prevent="goHome">
          <span class="lp-logo-mark">Z</span>
          <span class="lp-logo-text">智磐 AI</span>
        </a>
        <p class="lp-footer-copy">&copy; {{ currentYear }} 川岳智磐. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import "@/styles/landing-pages.css"

const router = useRouter()
const route = useRoute()
const currentYear = new Date().getFullYear()
const navScrolled = ref(false)
const mobileOpen = ref(false)

const onScroll = () => {
  navScrolled.value = window.scrollY > 20
}

onMounted(() => window.addEventListener("scroll", onScroll))
onUnmounted(() => window.removeEventListener("scroll", onScroll))

const goHome = () => {
  router.push("/home")
}

const goServices = () => {
  router.push("/home/services")
}

const goUsage = () => {
  router.push("/home/usage")
}

const goApp = () => {
  router.push("/remove")
}

const isActive = (path) => route.path === path

const serviceMatrix = [
  {
    title: "AI 素材生产咨询",
    desc: "围绕商品主图、场景图、视频与文案，设计适合企业团队的生产策略与规范。"
  },
  {
    title: "流程搭建与培训",
    desc: "帮助团队建立标准操作流程（SOP），并针对运营、设计岗位提供上手培训。"
  },
  {
    title: "模型与参数优化",
    desc: "结合行业与品类特征，优化提示词模板、比例策略与生成质量稳定性。"
  },
  {
    title: "数据化运营支持",
    desc: "连接流量词分析与素材迭代，形成“生成-投放-复盘”的闭环机制。"
  }
]

const processSteps = [
  {
    no: "01",
    title: "需求沟通与目标定义",
    desc: "明确业务阶段、素材规模、团队结构与指标目标，形成落地计划。"
  },
  {
    no: "02",
    title: "试点验证与模板沉淀",
    desc: "选择核心品类进行试跑，沉淀可复用的提示词模板与操作规范。"
  },
  {
    no: "03",
    title: "团队推广与系统接入",
    desc: "将试点能力拓展到多角色、多店铺，并与日常业务流程对齐。"
  },
  {
    no: "04",
    title: "持续优化与阶段复盘",
    desc: "按周或按月复盘效率、成本与质量指标，持续优化策略。"
  }
]

const scenarios = [
  {
    title: "品牌出海团队",
    desc: "适合需要快速多语种、多素材上新的品牌团队，显著提升内容产能。"
  },
  {
    title: "跨境卖家与代运营",
    desc: "帮助中小团队在有限人力下实现主图、视频、文案批量化生产。"
  },
  {
    title: "供应链与工厂型卖家",
    desc: "针对多款式、多颜色商品，快速完成裂变图与规格图的标准化输出。"
  }
]
</script>
